import React, { useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useClickOutside } from '../hooks/useClickOutside';

/**
 * A split button: one primary action on the left, a chevron on the right that opens a menu of the
 * secondary actions. Used on Case Detail so the header carries one button instead of a row.
 * With no menu items it renders the primary action as a plain button.
 */

export interface SplitAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Red text: destructive or negative actions (Reject, Delete). */
  danger?: boolean;
  /** A word of explanation, shown as the item's tooltip (never inline). */
  hint?: string;
}

interface Props {
  primary: SplitAction;
  items: Array<SplitAction | 'divider'>;
  /** 'primary' is the brand fill for the main workflow step; 'neutral' is a bordered button when
   *  the only thing left to do is edit or export. */
  tone?: 'primary' | 'neutral';
}

export default function SplitButton({ primary, items, tone = 'primary' }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  const Icon = primary.icon;

  const fill = tone === 'primary'
    ? 'bg-primary text-white hover:bg-primary/90 disabled:opacity-50'
    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';
  const divider = tone === 'primary' ? 'border-l border-white/40' : 'border-l-0';

  const mainBtn = (
    <button
      type="button"
      onClick={primary.onClick}
      disabled={primary.disabled}
      title={primary.title}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium shadow-sm ${fill} ${items.length ? 'rounded-l-md' : 'rounded-md'}`}
    >
      {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
      {primary.label}
    </button>
  );
  if (!items.length) return mainBtn;

  return (
    <div className="relative inline-flex" ref={ref}>
      {mainBtn}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        className={`inline-flex items-center px-2.5 py-2 text-sm font-medium shadow-sm rounded-r-md ${fill} ${divider}`}
      >
        {open ? <ChevronUpIcon className="h-4 w-4" aria-hidden="true" /> : <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-30 mt-1 min-w-[14rem] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-sm">
          {items.map((it, i) => {
            if (it === 'divider') return <div key={`d${i}`} className="my-1 border-t border-gray-100 dark:border-gray-700" />;
            const ItemIcon = it.icon;
            return (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                title={it.title ?? it.hint}
                onClick={() => { setOpen(false); it.onClick(); }}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed ${it.danger ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}
              >
                {ItemIcon ? <ItemIcon className={`h-5 w-5 shrink-0 ${it.danger ? 'text-red-500' : 'text-primary'}`} aria-hidden="true" /> : <span className="h-5 w-5 shrink-0" />}
                <span>{it.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
